import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, Plus, Trash2, Pencil, Star, Sparkles, EyeOff, Eye, X, Check, Ban, RefreshCw,
  LayoutDashboard, Users as UsersIcon, Database, Search, MapPin, CalendarDays, MessageSquare,
  Megaphone, Loader2, ArrowUpRight, ImageDown, Download, Utensils, Repeat, Image as ImageIcon, Store, Link2, Globe, Video as VideoIcon, Clock, Send,
  SlidersHorizontal, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, RotateCcw, Palette, Type, Layers, Monitor, Activity,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings, SettingsContext } from "@/context/SettingsContext";
import { CATEGORIES, SPORTS_TAGS, FITNESS_TAGS } from "@/lib/constants";
import {
  resolveSections, resolveMoods, resolveNavConfig, resolveTrailsList,
  DEFAULT_SECTIONS, DEFAULT_NAV, DEFAULT_THEME, FONT_PAIRINGS, applySettings,
} from "@/lib/layout";
import { SportIcon } from "@/components/CategoryIcon";
import { detectPlatform } from "@/lib/video";
import { dayLabel } from "@/lib/hours";
import { CATEGORY_COLORS } from "@/components/CategoryIcon";
import Home from "@/pages/Home";

const TABS = [
  { key: "Dashboard", icon: LayoutDashboard },
  { key: "Places", icon: MapPin },
  { key: "Events", icon: CalendarDays },
  { key: "Reviews", icon: MessageSquare },
  { key: "Ads", icon: Megaphone },
  { key: "Users", icon: UsersIcon },
  { key: "Site", icon: ImageIcon },
  { key: "Layout", icon: SlidersHorizontal },
  { key: "Videos", icon: VideoIcon },
  { key: "Health", icon: Activity },
  { key: "Photos", icon: ImageDown },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HEALTH_LABELS = { no_hours: "No hours", no_photo: "No photo", no_description: "No description", no_location: "No location", no_time: "No time", no_venue: "No venue" };

function Field({ label, ...props }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-muted-foreground">{label}</span>
      <input {...props} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
    </label>
  );
}
function Toggle({ on, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${on ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>
      {children}
    </button>
  );
}
function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Icon size={18} style={{ color: accent }} />
      </div>
      <p className="mt-2 font-serif text-4xl font-bold" style={{ color: accent }}>{value}</p>
    </div>
  );
}
const monthGrid = (y, m) => {
  const first = new Date(y, m, 1); const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
};

export default function Admin() {
  const { user } = useAuth();
  const { reloadSettings } = useSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("Dashboard");
  const [stats, setStats] = useState(null);
  const [places, setPlaces] = useState([]);
  const [events, setEvents] = useState([]);
  const [ads, setAds] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [placeSearch, setPlaceSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [eventView, setEventView] = useState("list");
  const [cursor, setCursor] = useState(new Date());
  const [runningJob, setRunningJob] = useState("");
  const [newPhoto, setNewPhoto] = useState("");
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [importForm, setImportForm] = useState({ area: "Aiken, SC", category: "Church", included_type: "church", max_results: 20 });
  const [importing, setImporting] = useState(false);
  const [presets, setPresets] = useState([]);
  const [runningPreset, setRunningPreset] = useState("");
  const [schedules, setSchedules] = useState({});
  const [newSpecial, setNewSpecial] = useState({ date: "", closed: false, open: "09:00", close: "17:00", note: "" });
  const [importUrl, setImportUrl] = useState("");
  const [importingUrl, setImportingUrl] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [customCounts, setCustomCounts] = useState({});
  const [newRow, setNewRow] = useState({ title: "", source_type: "category", value: "Food & Drink" });
  const [sponsorDays, setSponsorDays] = useState({});
  const [lastSection, setLastSection] = useState(null);
  const [previewNonce, setPreviewNonce] = useState(0);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("admin");
  const [inviteResult, setInviteResult] = useState(null);
  const [dragPhoto, setDragPhoto] = useState(null);
  const [googlePhotos, setGooglePhotos] = useState([]);
  const [loadingGPhotos, setLoadingGPhotos] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [savingEntity, setSavingEntity] = useState(false);
  const [videos, setVideos] = useState([]);
  const [pendingVideos, setPendingVideos] = useState([]);
  const [health, setHealth] = useState(null);
  const [healthFilter, setHealthFilter] = useState("all");
  const [bulkFilling, setBulkFilling] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");
  const [videoForm, setVideoForm] = useState({ title: "", platform: "youtube", url: "", embed_html: "", thumbnail: "", is_featured: false });
  const [needsCover, setNeedsCover] = useState(null);
  const [sweepOptions, setSweepOptions] = useState({});
  const [sweepLoading, setSweepLoading] = useState("");

  const importFromUrl = async () => {
    if (!importUrl.trim()) return toast.error("Paste a website link first");
    setImportingUrl(true);
    try {
      const { data } = await api.post("/admin/import-from-url", { url: importUrl.trim() });
      toast.success(`Added "${data.name}" to Needs Review — review it in the Places tab`);
      setImportUrl("");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed — try a different link");
    } finally {
      setImportingUrl(false);
    }
  };

  const loadAll = useCallback(() => {
    const editorOnly = user?.role === "editor";
    api.get("/places", { params: { include_hidden: true, limit: 500 } }).then((r) => setPlaces(r.data));
    api.get("/events", { params: { include_hidden: true, upcoming: false } }).then((r) => setEvents(r.data));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    if (editorOnly) return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/ads", { params: { active_only: false } }).then((r) => setAds(r.data));
    api.get("/reviews").then((r) => setReviews(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/imports/presets").then((r) => setPresets(r.data)).catch(() => {});
    api.get("/admin/schedules").then((r) => setSchedules(r.data)).catch(() => {});
    api.get("/admin/invites").then((r) => setInvites(r.data)).catch(() => {});
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/admin/videos/pending").then((r) => setPendingVideos(r.data)).catch(() => {});
  }, [user]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (user === false) navigate("/auth"); }, [user, navigate]);
  // Deep-link: /admin?editPlace=<slug> opens that place's editor directly.
  useEffect(() => {
    const slug = searchParams.get("editPlace");
    if (!slug || !places.length) return;
    const p = places.find((x) => x.slug === slug);
    if (p) { setTab("Places"); setEditing({ kind: "place", data: { ...p } }); }
    searchParams.delete("editPlace");
    setSearchParams(searchParams, { replace: true });
  }, [places, searchParams, setSearchParams]);
  // Deep-link: /admin?editEvent=<id> opens that event's editor directly.
  useEffect(() => {
    const eid = searchParams.get("editEvent");
    if (!eid || !events.length) return;
    const ev = events.find((x) => x.id === eid);
    if (ev) { setTab("Events"); setEditing({ kind: "event", data: { ...ev, start_datetime: ev.start_datetime?.slice(0, 16) } }); }
    searchParams.delete("editEvent");
    setSearchParams(searchParams, { replace: true });
  }, [events, searchParams, setSearchParams]);

  // Populate layout-editor lists from code defaults the first time (so admin sees the full list).
  useEffect(() => {
    if (!settings) return;
    const norm = {};
    const mergedSections = resolveSections(settings).map((s) => ({ key: s.key, label: s.label, visible: s.visible !== false }));
    if (mergedSections.length !== (settings.home_sections?.length || 0)) norm.home_sections = mergedSections;
    if (!settings.moods?.length) norm.moods = resolveMoods(settings).map((m) => ({ key: m.key, label: m.label, query: m.query, visible: m.visible !== false }));
    if (!settings.nav_items?.length) norm.nav_items = resolveNavConfig(settings).map((n) => ({ key: n.key, label: n.label, path: n.path, visible: n.visible !== false }));
    if (!settings.trails?.length) norm.trails = resolveTrailsList(settings).map((t, i) => ({ slug: t.slug, title: t.title, visible: true, order: i }));
    if (!settings.theme || Object.keys(settings.theme).length === 0) norm.theme = { ...DEFAULT_THEME };
    if (Object.keys(norm).length) setSettings((s) => ({ ...s, ...norm }));
  }, [settings]);

  // Count how many places match each custom row (for the empty-row hint in the editor).
  useEffect(() => {
    (settings?.custom_rows || []).forEach((r) => {
      const params = r.source_type === "tag" ? { tag: r.value } : { category: r.value };
      api.get("/places", { params }).then((res) => setCustomCounts((c) => ({ ...c, [r.id]: res.data.length }))).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify((settings?.custom_rows || []).map((r) => `${r.id}:${r.source_type}:${r.value}`))]);

  useEffect(() => {
    if (user && user.role === "editor" && !["Places", "Events"].includes(tab)) setTab("Places");
  }, [user, tab]);

  if (user === null) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (user === false) return null;
  const isEditor = user.role === "editor";
  if (user.role !== "admin" && !isEditor) return <div className="p-16 text-center text-muted-foreground">Admin access only.</div>;
  const visibleTabs = isEditor ? TABS.filter((t) => ["Places", "Events"].includes(t.key)) : TABS;

  const patchPlace = async (id, u) => { await api.put(`/places/${id}`, u); loadAll(); };
  const delPlace = async (id) => { await api.delete(`/places/${id}`); toast.success("Place deleted"); loadAll(); };
  const patchEvent = async (id, u) => { await api.put(`/events/${id}`, u); loadAll(); };
  const delEvent = async (id) => { await api.delete(`/events/${id}`); toast.success("Event deleted"); loadAll(); };
  const patchAd = async (id, u) => { await api.put(`/ads/${id}`, u); loadAll(); };
  const delAd = async (id) => { await api.delete(`/ads/${id}`); toast.success("Ad deleted"); loadAll(); };
  const moderate = async (id, status) => { await api.put(`/reviews/${id}/status`, null, { params: { status } }); toast.success(`Review ${status}`); loadAll(); };

  const refreshPlaceGoogle = async (id) => {
    try { const { data } = await api.post(`/admin/places/${id}/refresh-google`); toast.success(data.updated ? "Refreshed from Google" : "No data returned"); loadAll(); }
    catch { toast.info("Add a Google Places API key to enable live data."); }
  };
  const backfillPhotos = async () => {
    try {
      await api.post("/admin/google/backfill-photos");
      toast.success("Photo backfill started — real Google photos will replace stock images shortly. Refresh in a minute.");
    } catch (e) { toast.info(e.response?.data?.detail || "Add a Google Places API key to enable this."); }
  };
  const backfillCoords = async () => {
    try {
      await api.post("/admin/google/backfill-coords");
      toast.success("Map-pin backfill started — missing locations will fill in from Google shortly.");
    } catch (e) { toast.info(e.response?.data?.detail || "Add a Google Places API key to enable this."); }
  };
  const refreshPlaceDetails = async (id) => {
    try { const { data } = await api.post(`/admin/places/${id}/refresh-details`); toast.success(`Updated — pulled ${data.photos} photo(s)`); loadAll(); }
    catch (e) { toast.info(e.response?.data?.detail || "Add a Google Places API key to enable this."); }
  };
  const startImport = async () => {
    setImporting(true);
    try {
      await api.post("/admin/imports/places", importForm);
      toast.success("Import started — watch progress below");
      let n = 0;
      const iv = setInterval(async () => { n += 1; await loadAll(); if (n > 12) clearInterval(iv); }, 3000);
    } catch { toast.error("Could not start import"); }
    finally { setImporting(false); }
  };
  const startPreset = async (key) => {
    setRunningPreset(key);
    try {
      await api.post("/admin/imports/preset", { preset: key });
      toast.success("Import started — sweeping the CSRA. Watch progress below.");
      let n = 0;
      const iv = setInterval(async () => { n += 1; await loadAll(); if (n > 20) clearInterval(iv); }, 3000);
    } catch { toast.error("Could not start import"); }
    finally { setRunningPreset(""); }
  };
  const toggleSchedule = async (key, enabled) => {
    setSchedules((s) => ({ ...s, [key]: enabled }));
    try { await api.put(`/admin/schedules/${key}`, { enabled }); toast.success(enabled ? "Weekly auto-import on" : "Weekly auto-import off"); }
    catch { toast.error("Failed"); loadAll(); }
  };
  const bulkPlaces = async (action) => {
    if (action === "reject" && !window.confirm("Reject and delete ALL places awaiting review?")) return;
    try {
      const { data } = await api.post("/admin/places/bulk", { action });
      toast.success(action === "approve" ? `Approved ${data.approved} places` : `Rejected ${data.rejected} places`);
      loadAll();
    } catch { toast.error("Bulk action failed"); }
  };
  const runJob = async (type) => {
    setRunningJob(type);
    try { const { data } = await api.post("/admin/imports/run", { type }); toast.success(data.message || `Job ${data.status}`); loadAll(); }
    catch { toast.error("Job failed"); }
    finally { setRunningJob(""); }
  };
  const setRole = async (uid, role) => {
    try { await api.put(`/admin/users/${uid}/role`, null, { params: { role } }); toast.success("Role updated"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delUser = async (uid) => {
    try { await api.delete(`/admin/users/${uid}`); toast.success("User removed"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadPlacePhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingPhoto(true); setUploadProgress(0);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const url = `${BACKEND}${data.url}`;
      setEditing((ed) => ({ ...ed, data: { ...ed.data, photos: [...(ed.data.photos || []), url], cover_image: ed.data.cover_image || url } }));
      toast.success("Photo added");
    } catch { toast.error("Upload failed"); }
    finally { setUploadingPhoto(false); setUploadProgress(0); e.target.value = ""; }
  };
  const addPhotoUrl = () => {
    if (!newPhoto) return;
    setEditing((ed) => ({ ...ed, data: { ...ed.data, photos: [...(ed.data.photos || []), newPhoto], cover_image: ed.data.cover_image || newPhoto } }));
    setNewPhoto("");
  };
  const removePhoto = (url) => setEditing((ed) => {
    const photos = (ed.data.photos || []).filter((p) => p !== url);
    const cover = ed.data.cover_image === url ? (photos[0] || "") : ed.data.cover_image;
    return { ...ed, data: { ...ed.data, photos, cover_image: cover } };
  });
  const pickGooglePhotos = async () => {
    if (!editing?.data?.id) return toast.info("Save the listing first, then pick Google photos");
    setLoadingGPhotos(true); setGooglePhotos([]);
    try {
      const { data } = await api.get(`/admin/places/${editing.data.id}/google-photos`);
      if (!data.photos?.length) toast.info("No Google photos found for this listing");
      setGooglePhotos(data.photos || []);
    } catch (e) { toast.info(e.response?.data?.detail || "Add a Google Places API key to enable this."); }
    finally { setLoadingGPhotos(false); }
  };
  const applyGooglePhoto = (url) => setEditing((ed) => {
    const photos = (ed.data.photos || []).includes(url) ? ed.data.photos : [...(ed.data.photos || []), url];
    return { ...ed, data: { ...ed.data, photos, cover_image: url } };
  });
  const reorderPhoto = (from, to) => {
    if (from === null || from === to) { setDragPhoto(null); return; }
    setEditing((ed) => {
      const photos = [...(ed.data.photos || [])];
      const [moved] = photos.splice(from, 1);
      photos.splice(to, 0, moved);
      return { ...ed, data: { ...ed.data, photos } };
    });
    setDragPhoto(null);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/settings", settings);
      setSettings(data);
      applySettings(data);
      setPreviewNonce((n) => n + 1);
      if (reloadSettings) reloadSettings();
      toast.success("Settings saved");
    }
    catch { toast.error("Failed to save settings"); }
    finally { setSavingSettings(false); }
  };
  const uploadHeroImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setSettings((s) => ({ ...s, hero_image: `${BACKEND}${data.url}` }));
      toast.success("Image uploaded — click Save to publish");
    } catch { toast.error("Upload failed"); }
  };

  // ---- Layout editor helpers ----
  const moveItem = (field, i, dir) => {
    setSettings((s) => {
      const arr = [...(s[field] || [])];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return s;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...s, [field]: arr };
    });
  };
  const toggleItem = (field, i) => {
    setSettings((s) => {
      const arr = [...(s[field] || [])];
      arr[i] = { ...arr[i], visible: arr[i].visible === false };
      return { ...s, [field]: arr };
    });
  };
  const renameItem = (field, i, label) => {
    setSettings((s) => {
      const arr = [...(s[field] || [])];
      arr[i] = { ...arr[i], label };
      return { ...s, [field]: arr };
    });
  };
  const resetLayout = (field) => {
    const defaults = {
      home_sections: DEFAULT_SECTIONS.map((x) => ({ ...x, visible: true })),
      moods: resolveMoods({}).map((m) => ({ key: m.key, label: m.label, query: m.query, visible: true })),
      nav_items: DEFAULT_NAV.map((x) => ({ ...x, visible: true })),
      trails: resolveTrailsList({}).map((t, i) => ({ slug: t.slug, title: t.title, visible: true, order: i })),
    };
    setSettings((s) => ({ ...s, [field]: defaults[field] }));
    toast.success("Reset to defaults — click Save to publish");
  };
  const setTheme = (patch) => {
    setSettings((s) => {
      const theme = { ...(s.theme || DEFAULT_THEME), ...patch };
      applySettings({ theme });
      return { ...s, theme };
    });
  };
  const resetTheme = () => {
    setSettings((s) => { applySettings({ theme: DEFAULT_THEME }); return { ...s, theme: { ...DEFAULT_THEME } }; });
    toast.success("Colors & font reset — click Save to publish");
  };

  // ---- Featured ordering ----
  const orderedFeatured = (() => {
    const fp = places.filter((p) => p.is_featured);
    const order = settings?.featured_order || [];
    return [...fp].sort((a, b) => {
      const ia = order.indexOf(a.id), ib = order.indexOf(b.id);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  })();
  const moveFeatured = (i, dir) => {
    const list = orderedFeatured.map((p) => p.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    setSettings((s) => ({ ...s, featured_order: list }));
  };
  const pinPlace = async (id, on) => {
    try {
      await api.put(`/places/${id}`, { is_featured: on });
      setSettings((s) => {
        let ord = [...(s.featured_order || [])];
        if (on) { if (!ord.includes(id)) ord.push(id); } else { ord = ord.filter((x) => x !== id); }
        return { ...s, featured_order: ord };
      });
      toast.success(on ? "Pinned to Featured — Save to lock the order" : "Removed from Featured");
      loadAll();
    } catch { toast.error("Failed to update listing"); }
  };

  // ---- Custom home rows ----
  const addCustomRow = () => {
    const title = newRow.title.trim();
    const value = (newRow.value || "").trim();
    if (!title || !value) { toast.error("Give the row a title and a category/tag"); return; }
    const id = `${Date.now()}`;
    const row = { id, title, source_type: newRow.source_type, value };
    setSettings((s) => ({
      ...s,
      custom_rows: [...(s.custom_rows || []), row],
      home_sections: [...(s.home_sections || []), { key: `custom_${id}`, label: title, visible: true }],
    }));
    setNewRow({ title: "", source_type: "category", value: "Food & Drink" });
    toast.success("Row added — Save & publish to make it live");
  };
  const removeCustomRow = (id) => {
    setSettings((s) => ({
      ...s,
      custom_rows: (s.custom_rows || []).filter((r) => r.id !== id),
      home_sections: (s.home_sections || []).filter((x) => x.key !== `custom_${id}`),
    }));
    toast.success("Row removed — Save & publish to apply");
  };
  const setSponsored = async (id, on, days) => {
    try {
      const d = Number(days) || 30;
      const until = on ? new Date(Date.now() + d * 86400000).toISOString() : null;
      await api.put(`/places/${id}`, { is_sponsored: on, sponsored_until: until });
      toast.success(on ? `Now shows the Sponsored badge (${d} days)` : "Now shows the Featured badge");
      loadAll();
    } catch { toast.error("Failed to update badge"); }
  };
  const extendSponsor = (p) => {
    const now = Date.now();
    const base = p.sponsored_until && new Date(p.sponsored_until).getTime() > now ? new Date(p.sponsored_until).getTime() : now;
    const days = Math.ceil((base + 30 * 86400000 - now) / 86400000);
    setSponsored(p.id, true, days);
  };
  const cancelAutoRenew = async (id) => {
    if (!window.confirm("Turn off auto-renew for this listing? It stays active until the current period ends and won't renew after that.")) return;
    try {
      await api.post(`/payments/subscriptions/${id}/cancel`);
      toast.success("Auto-renew turned off");
      loadAll();
    } catch { toast.error("Could not turn off auto-renew"); }
  };
  const uploadHeroVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setSettings((s) => ({ ...s, hero_video: `${BACKEND}${data.url}` }));
      toast.success("Video uploaded — click Save to publish");
    } catch { toast.error("Upload failed"); }
  };
  const uploadEventCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setEditing((ed) => ({ ...ed, data: { ...ed.data, cover_image: `${BACKEND}${data.url}` } }));
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
  };
  const loadVideos = () => {
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/admin/videos/pending").then((r) => setPendingVideos(r.data)).catch(() => {});
  };
  const loadHealth = () => api.get("/admin/data-health").then((r) => setHealth(r.data)).catch(() => toast.error("Scan failed"));
  const bulkFill = async () => {
    if (bulkFilling) return;
    setBulkFilling(true);
    setBulkMsg("Starting…");
    try {
      const { data } = await api.post("/admin/data-health/bulk-fill");
      const jobId = data.job_id || data.id;
      if (!jobId) { setBulkMsg(""); setBulkFilling(false); toast.error("Failed to start bulk fill"); return; }
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/admin/jobs/${jobId}`);
          const filled = job.created ?? 0;
          const hours = job.photos ?? 0;
          const found = job.found ?? 0;
          const total = job.count ?? 0;
          const msg = job.message || `Processed ${found}/${total} • ${filled} descriptions, ${hours} hours filled`;
          setBulkMsg(msg);
          if (job.status === "done" || job.status === "completed" || job.status === "error" || job.status === "failed") {
            setBulkFilling(false);
            if (job.status === "error" || job.status === "failed") toast.error("Bulk fill failed");
            else toast.success(`Bulk fill complete — ${filled} descriptions, ${hours} hours`);
            loadHealth();
            return;
          }
          setTimeout(poll, 3000);
        } catch (e) {
          setBulkFilling(false);
          setBulkMsg("");
          toast.error("Bulk fill status check failed");
        }
      };
      setTimeout(poll, 1500);
    } catch (e) {
      setBulkFilling(false);
      setBulkMsg("");
      toast.error("Failed to start bulk fill");
    }
  };
  const approveVideo = async (id) => {
    try { await api.post(`/admin/videos/${id}/approve`); toast.success("Video approved & published"); loadVideos(); }
    catch { toast.error("Approve failed"); }
  };
  const rejectVideo = async (id) => {
    if (!window.confirm("Reject and delete this submission?")) return;
    try { await api.delete(`/videos/${id}`); toast.success("Submission rejected"); loadVideos(); }
    catch { toast.error("Reject failed"); }
  };
  const saveVideo = async () => {
    if (!videoForm.title || (!videoForm.url && !videoForm.embed_html)) return toast.error("Add a title and either a video URL or embed code");
    const platform = videoForm.platform || detectPlatform(videoForm.url);
    try {
      if (videoForm.id) await api.put(`/videos/${videoForm.id}`, { ...videoForm, platform });
      else await api.post("/videos", { ...videoForm, platform });
      toast.success("Video saved");
      setVideoForm({ title: "", platform: "youtube", url: "", embed_html: "", thumbnail: "", is_featured: false });
      loadVideos();
    } catch { toast.error("Save failed"); }
  };
  const uploadVideoThumb = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setVideoForm((f) => ({ ...f, thumbnail: `${BACKEND}${data.url}` }));
      toast.success("Poster image added");
    } catch { toast.error("Upload failed"); }
  };
  const backfillThumbnails = async () => {
    try {
      const { data } = await api.post("/admin/videos/backfill-thumbnails");
      toast.success(`Refreshed ${data.filled} thumbnail${data.filled === 1 ? "" : "s"}`);
      loadVideos();
    } catch { toast.error("Couldn't refresh thumbnails"); }
  };
  const uploadVideoFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setVideoForm((f) => ({ ...f, url: `${BACKEND}${data.url}`, platform: "file" }));
      toast.success("Video uploaded — add a title and Save");
    } catch { toast.error("Upload failed"); }
  };
  const deleteVideo = async (id) => { if (!window.confirm("Delete this video?")) return; await api.delete(`/videos/${id}`); loadVideos(); };
  const toggleVideoFeature = async (v) => { await api.put(`/videos/${v.id}`, { is_featured: !v.is_featured }); loadVideos(); };
  const moveVideo = async (i, dir) => {
    const arr = [...videos]; const j = i + dir; if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; setVideos(arr);
    try { await api.put("/videos/reorder", { ids: arr.map((v) => v.id) }); } catch (err) { console.error("Video reorder failed:", err); toast.error("Couldn't save the new order"); loadVideos(); }
  };
  const loadNeedsCover = () => api.get("/admin/places/needs-cover").then((r) => setNeedsCover(r.data)).catch(() => {});
  const loadSweepPhotos = async (id) => {
    setSweepLoading(id);
    try { const { data } = await api.get(`/admin/places/${id}/google-photos`); setSweepOptions((o) => ({ ...o, [id]: data.photos || [] })); if (!data.photos?.length) toast.info("No Google photos found for this listing"); }
    catch (e) { toast.info(e.response?.data?.detail || "Add a Google Places API key to enable this."); }
    finally { setSweepLoading(""); }
  };
  const applySweepCover = async (id, url) => {
    try {
      await api.put(`/places/${id}`, { cover_image: url, photos: [url] });
      toast.success("Cover updated");
      setNeedsCover((nc) => nc ? { ...nc, count: nc.count - 1, places: nc.places.filter((p) => p.id !== id) } : nc);
      setSweepOptions((o) => { const n = { ...o }; delete n[id]; return n; });
    } catch { toast.error("Could not update cover"); }
  };
  const bulkAutoCover = async () => {
    try {
      await api.post("/admin/google/backfill-photos");
      toast.success("Auto-picking the best Google cover for every stock listing — rescan in a minute to see results.");
    } catch (e) { toast.info(e.response?.data?.detail || "Add a Google Places API key to enable this."); }
  };
  const clearSeedHours = async () => {
    if (!window.confirm("Clear the generic 9–5 placeholder hours from listings that don't have real hours? They'll show 'hours not listed' instead of a Closed badge. Locked hours are kept.")) return;
    try {
      const { data } = await api.post("/admin/places/clear-seed-hours");
      toast.success(`Cleared placeholder hours on ${data.cleared} listing(s).`);
      loadAll();
    } catch { toast.error("Could not clear placeholder hours"); }
  };
  const createInvite = async () => {
    try {
      const { data } = await api.post("/admin/invites", { email: inviteEmail.trim() || null, role: inviteRole, base_url: window.location.origin });
      setInviteResult(data);
      setInviteEmail("");
      toast.success(data.emailed ? "Invite created & emailed" : "Invite link created — copy it below");
      loadAll();
    } catch { toast.error("Could not create invite"); }
  };
  const revokeInvite = async (id) => {
    try { await api.delete(`/admin/invites/${id}`); toast.success("Invite revoked"); loadAll(); }
    catch { toast.error("Failed to revoke"); }
  };
  const copyInviteLink = (link) => { navigator.clipboard.writeText(link).then(() => toast.success("Link copied to clipboard")); };

  const layoutRow = (field, item, i, len, editableLabel) => {
    const id = item.key || item.slug;
    const shown = item.visible !== false;
    return (
      <div key={id} className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2" data-testid={`layout-${field}-row-${id}`}>
        <div className="flex flex-col">
          <button disabled={i === 0} onClick={() => { moveItem(field, i, -1); if (field === "home_sections") setLastSection(id); }} data-testid={`layout-${field}-up-${id}`} className="text-muted-foreground hover:text-primary disabled:opacity-30"><ArrowUp size={14} /></button>
          <button disabled={i === len - 1} onClick={() => { moveItem(field, i, 1); if (field === "home_sections") setLastSection(id); }} data-testid={`layout-${field}-down-${id}`} className="text-muted-foreground hover:text-primary disabled:opacity-30"><ArrowDown size={14} /></button>
        </div>
        {editableLabel ? (
          <input value={item.label || ""} onChange={(e) => renameItem(field, i, e.target.value)} data-testid={`layout-${field}-label-${id}`}
            className="flex-1 rounded border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary" />
        ) : (
          <span className="flex-1 text-sm font-medium">{item.label || item.title}</span>
        )}
        <button onClick={() => { toggleItem(field, i); if (field === "home_sections") setLastSection(id); }} data-testid={`layout-${field}-toggle-${id}`}
          className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${shown ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>
          {shown ? <><Eye size={12} /> Shown</> : <><EyeOff size={12} /> Hidden</>}
        </button>
      </div>
    );
  };

  const saveEntity = async () => {
    if (uploadingPhoto) { toast.info("Please wait — a photo is still uploading"); return; }
    const { kind, data } = editing;
    setSavingEntity(true);
    try {
      if (kind === "place") { data.id ? await api.put(`/places/${data.id}`, data) : await api.post("/places", data); }
      else if (kind === "event") { data.id ? await api.put(`/events/${data.id}`, data) : await api.post("/events", data); }
      else if (kind === "ad") { data.id ? await api.put(`/ads/${data.id}`, data) : await api.post("/ads", data); }
      toast.success("Saved"); setEditing(null); loadAll();
    } catch { toast.error("Save failed — check required fields"); }
    finally { setSavingEntity(false); }
  };

  const pendingCount = reviews.filter((r) => r.status === "pending").length;
  const filteredPlaces = places.filter((p) => (p.name.toLowerCase().includes(placeSearch.toLowerCase()) || (p.city || "").toLowerCase().includes(placeSearch.toLowerCase())) && (!needsReviewOnly || p.status === "needs_review"));
  const filteredUsers = users.filter((u) => u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase()));
  const eventsByDay = events.reduce((acc, e) => { const k = new Date(e.start_datetime).toDateString(); (acc[k] = acc[k] || []).push(e); return acc; }, {});
  const days = monthGrid(cursor.getFullYear(), cursor.getMonth());

  return (
    <div className="px-4 py-6 md:px-8">
      <h1 className="mb-5 flex items-center gap-2 font-serif text-3xl font-bold md:text-4xl"><Shield className="text-secondary" /> Admin</h1>

      <div className="no-scrollbar mb-6 flex gap-2 overflow-x-auto border-b border-border">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key.toLowerCase()}`}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-t-lg px-4 py-2 text-sm font-semibold ${tab === t.key ? "bg-card text-primary" : "text-muted-foreground"}`}>
              <Icon size={15} /> {t.key}
              {t.key === "Reviews" && pendingCount > 0 && <span className="rounded-full bg-primary px-1.5 text-xs text-primary-foreground">{pendingCount}</span>}
              {t.key === "Places" && stats?.needs_review_places > 0 && <span data-testid="tab-badge-places" className="rounded-full bg-primary px-1.5 text-xs text-primary-foreground">{stats.needs_review_places}</span>}
              {t.key === "Videos" && pendingVideos.length > 0 && <span data-testid="tab-badge-videos" className="rounded-full bg-primary px-1.5 text-xs text-primary-foreground">{pendingVideos.length}</span>}
            </button>
          );
        })}
      </div>

      {/* DASHBOARD */}
      {tab === "Dashboard" && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <StatCard label="Pending submissions" value={stats.pending_submissions} icon={Store} accent="#B26A00" testid="stat-pending-submissions" />
            <StatCard label="Places" value={stats.places} icon={MapPin} accent="#C0492E" testid="stat-places" />
            <StatCard label="Events" value={stats.events} icon={CalendarDays} accent="#9B4DCA" testid="stat-events" />
            <StatCard label="Pending reviews" value={stats.pending_reviews} icon={MessageSquare} accent="#B8860B" testid="stat-pending" />
            <StatCard label="Active sponsorships" value={stats.active_sponsorships} icon={Megaphone} accent="#2E7D5B" testid="stat-sponsorships" />
            <StatCard label="Sponsored places" value={stats.sponsored_places} icon={Sparkles} accent="#C77D4A" testid="stat-sponsored" />
            <StatCard label="Users" value={stats.users} icon={UsersIcon} accent="#5B6B8C" testid="stat-users" />
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-serif text-xl font-semibold"><Database size={18} /> Data import & enrichment</h2>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${stats.google_enabled ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`}>
                Google API {stats.google_enabled ? "connected" : "not configured"}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => runJob("google_enrichment")} disabled={!!runningJob} data-testid="job-google-enrichment"
                className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
                {runningJob === "google_enrichment" ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Enrich from Google
              </button>
              <button onClick={() => runJob("recount_reviews")} disabled={!!runningJob} data-testid="job-recount-reviews"
                className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold disabled:opacity-60">
                {runningJob === "recount_reviews" ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Recount community ratings
              </button>
              {stats.google_enabled && (
                <button onClick={backfillPhotos} data-testid="job-backfill-photos"
                  className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold">
                  <ImageDown size={15} /> Backfill real photos
                </button>
              )}
              {stats.google_enabled && (
                <button onClick={backfillCoords} data-testid="job-backfill-coords"
                  className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold">
                  <MapPin size={15} /> Backfill map pins
                </button>
              )}
            </div>
            <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="url-import-card">
              <h3 className="flex items-center gap-2 text-sm font-semibold"><Globe size={15} className="text-primary" /> Add a business by website link</h3>
              <p className="mb-2 mt-0.5 text-xs text-muted-foreground">Paste a URL and we'll crawl it for the name, description, photos & details, then drop it into Needs Review.</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} data-testid="url-import-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !importingUrl) importFromUrl(); }}
                  placeholder="https://example-restaurant.com"
                  className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                <button onClick={importFromUrl} disabled={importingUrl} data-testid="url-import-btn"
                  className="flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
                  {importingUrl ? <><Loader2 size={15} className="animate-spin" /> Crawling…</> : <><Link2 size={15} /> Import from website</>}
                </button>
              </div>
            </div>
            <h3 className="mb-2 mt-5 text-sm font-semibold text-muted-foreground">Recent jobs</h3>
            {stats.recent_imports?.length ? (
              <div className="space-y-1.5">
                {stats.recent_imports.map((j) => (
                  <div key={j.id} className="flex items-center gap-3 rounded-lg border border-border bg-background p-2 text-sm">
                    <span className="font-medium">{j.type}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${j.status === "completed" ? "bg-secondary/15 text-secondary" : j.status === "running" ? "bg-accent/20 text-accent-foreground" : j.status === "skipped" ? "bg-muted text-muted-foreground" : "bg-destructive/15 text-destructive"}`}>{j.status}</span>
                    {(j.found !== undefined && j.found !== null) && <span className="text-xs text-muted-foreground">found {j.found ?? 0} · created {j.created ?? 0} · dupes {j.duplicates ?? 0} · photos {j.photos ?? 0}</span>}
                    <span className="truncate text-xs text-muted-foreground">{j.message}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{j.started_at ? new Date(j.started_at).toLocaleString() : ""}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No jobs run yet.</p>}
          </div>

          {/* Quick preset imports — Food & Drink + local businesses */}
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="mb-1 flex items-center gap-2 font-serif text-xl font-semibold"><Utensils size={18} /> Quick imports — one tap</h2>
            <p className="mb-4 text-sm text-muted-foreground">Ready-to-run sweeps across all CSRA cities (Aiken, North Augusta, Augusta, Edgefield, Graniteville). Prioritizes Food &amp; Drink and local businesses. New places land in <b>Needs Review</b>; duplicates are skipped.</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {presets.map((p) => (
                <div key={p.key} data-testid={`preset-${p.key}`}
                  className="flex flex-col items-start gap-1 rounded-2xl border border-border bg-background p-4 text-left transition-all hover:border-primary">
                  <button onClick={() => startPreset(p.key)} disabled={!!runningPreset}
                    data-testid={`preset-run-${p.key}`}
                    className="flex w-full flex-col items-start gap-1 text-left transition-all hover:-translate-y-0.5 disabled:opacity-60">
                    <span className="flex items-center gap-2 font-serif text-base font-bold">
                      {runningPreset === p.key ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} className="text-primary" />} {p.label}
                    </span>
                    <span className="text-xs text-muted-foreground">Assigns → {p.category} · {p.types.length} place types × {p.areas} cities</span>
                  </button>
                  <label className="mt-3 flex w-full cursor-pointer items-center justify-between gap-2 border-t border-border pt-3 text-xs font-medium text-muted-foreground">
                    <span className="flex items-center gap-1"><Repeat size={12} /> Auto-run weekly</span>
                    <input type="checkbox" data-testid={`schedule-toggle-${p.key}`}
                      checked={!!schedules[p.key]}
                      onChange={(e) => toggleSchedule(p.key, e.target.checked)}
                      className="h-4 w-4 accent-primary" />
                  </label>
                </div>
              ))}
            </div>
            {!stats.google_enabled && <p className="mt-3 text-xs text-muted-foreground">⚠️ Add a <b>GOOGLE_PLACES_API_KEY</b> to run live imports — jobs report "skipped" until then.</p>}
          </div>

          {/* Bulk import from Google */}
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="mb-1 flex items-center gap-2 font-serif text-xl font-semibold"><Download size={18} /> Bulk import places from Google</h2>
            <p className="mb-4 text-sm text-muted-foreground">Search the CSRA by category & area. New places are downloaded (name, address, phone, website, hours, rating, real photos) into <b>Needs Review</b> so you can approve them. Duplicates are skipped automatically.</p>
            <div data-testid="google-sync-status" className="mb-4 flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-xs">
              <span className={`h-2 w-2 rounded-full ${stats.google_enabled ? "bg-green-500" : "bg-red-400"}`} />
              {stats.google_enabled
                ? <span className="font-medium text-green-700">Google Hours Sync ON — ratings & opening hours auto-refresh every 12 hours.</span>
                : <span className="text-muted-foreground">Google Hours Sync OFF — add <b>GOOGLE_PLACES_API_KEY</b> to enable automatic hours & ratings refresh.</span>}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Area</span>
                <input value={importForm.area} onChange={(e) => setImportForm({ ...importForm, area: e.target.value })} data-testid="import-area"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" /></label>
              <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Assign category</span>
                <select value={importForm.category} onChange={(e) => setImportForm({ ...importForm, category: e.target.value })} data-testid="import-category"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">{CATEGORIES.concat("Other").map((c) => <option key={c}>{c}</option>)}</select></label>
              <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Google type</span>
                <select value={importForm.included_type} onChange={(e) => setImportForm({ ...importForm, included_type: e.target.value })} data-testid="import-type"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                  <option value="">Any (use text search)</option>
                  <option value="church">Church / place of worship</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="bar">Bar</option>
                  <option value="night_club">Nightlife</option>
                  <option value="cafe">Cafe</option>
                  <option value="tourist_attraction">Attraction</option>
                  <option value="museum">Museum</option>
                  <option value="art_gallery">Art gallery</option>
                  <option value="park">Park / outdoors</option>
                  <option value="shopping_mall">Shopping</option>
                  <option value="store">Store</option>
                </select></label>
              <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Max results</span>
                <select value={importForm.max_results} onChange={(e) => setImportForm({ ...importForm, max_results: parseInt(e.target.value, 10) })} data-testid="import-max"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"><option value={10}>10</option><option value={20}>20</option></select></label>
            </div>
            <button onClick={startImport} disabled={importing} data-testid="start-import-btn"
              className="mt-4 flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {importing ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Start import
            </button>
            {!stats.google_enabled && <p className="mt-2 text-xs text-muted-foreground">⚠️ Add a <b>GOOGLE_PLACES_API_KEY</b> to the backend to run live imports — jobs will report "skipped" until then.</p>}
          </div>
        </div>
      )}

      {/* PLACES */}
      {tab === "Places" && (
        <div>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <button onClick={() => setEditing({ kind: "place", data: { category: "Food & Drink", state: "SC", status: "published", photos: [] } })}
              data-testid="admin-new-place" className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"><Plus size={16} /> New Place</button>
            <div className="relative flex-1 md:max-w-xs">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={placeSearch} onChange={(e) => setPlaceSearch(e.target.value)} placeholder="Search places…" data-testid="admin-place-search"
                className="w-full rounded-full border border-border bg-card py-2 pl-9 pr-4 text-sm outline-none focus:border-primary" />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <input type="checkbox" checked={needsReviewOnly} onChange={(e) => setNeedsReviewOnly(e.target.checked)} data-testid="needs-review-filter" /> Needs review only
            </label>
          </div>
          {places.some((p) => p.status === "needs_review") && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-accent/40 bg-accent/10 p-3 text-sm">
              <span className="font-semibold">{places.filter((p) => p.status === "needs_review").length} places awaiting review</span>
              <button onClick={() => bulkPlaces("approve")} data-testid="bulk-approve-btn" className="ml-auto flex items-center gap-1 rounded-full bg-secondary px-4 py-1.5 text-xs font-semibold text-secondary-foreground"><Check size={13} /> Approve all</button>
              <button onClick={() => bulkPlaces("reject")} data-testid="bulk-reject-btn" className="flex items-center gap-1 rounded-full border border-border bg-card px-4 py-1.5 text-xs font-semibold text-destructive"><Ban size={13} /> Reject all</button>
            </div>
          )}
          <div className="space-y-2">
            {filteredPlaces.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`admin-place-${p.slug}`}>
                <a href={`/place/${p.slug}`} target="_blank" rel="noreferrer" title="Open public listing" data-testid={`admin-view-place-img-${p.slug}`} className="shrink-0">
                  <img src={p.cover_image} alt="" className="h-12 w-12 rounded-lg object-cover transition-opacity hover:opacity-80" />
                </a>
                <div className="min-w-0 flex-1">
                  <a href={`/place/${p.slug}`} target="_blank" rel="noreferrer" title="Open public listing" data-testid={`admin-view-place-${p.slug}`} className="inline-flex max-w-full items-center gap-1 font-semibold hover:text-primary hover:underline">
                    <span className="truncate">{p.name}</span>
                    <ArrowUpRight size={13} className="shrink-0 opacity-60" />
                    {p.status === "needs_review" && <span className="ml-1 shrink-0 rounded-full bg-accent/20 px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">Needs review</span>}
                  </a>
                  <p className="text-xs text-muted-foreground">{p.category} · {p.city}</p>
                </div>
                <Toggle on={p.is_featured} testid={`toggle-featured-${p.slug}`} onClick={() => patchPlace(p.id, { is_featured: !p.is_featured })}><Star size={12} /> Featured</Toggle>
                <Toggle on={p.is_sponsored} testid={`toggle-sponsored-${p.slug}`} onClick={() => patchPlace(p.id, { is_sponsored: !p.is_sponsored })}><Sparkles size={12} /> Sponsored</Toggle>
                <Toggle on={p.is_hidden} testid={`toggle-hidden-${p.slug}`} onClick={() => patchPlace(p.id, { is_hidden: !p.is_hidden })}>{p.is_hidden ? <EyeOff size={12} /> : <Eye size={12} />} {p.is_hidden ? "Hidden" : "Visible"}</Toggle>
                {p.status === "needs_review" && (
                  <button onClick={() => patchPlace(p.id, { status: "published", is_hidden: false })} data-testid={`approve-place-${p.slug}`}
                    className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground"><Check size={12} /> Approve</button>
                )}
                <button onClick={() => refreshPlaceDetails(p.id)} title="Refresh photos & details" data-testid={`refresh-details-${p.slug}`} className="rounded-full p-2 hover:bg-muted"><ImageDown size={15} /></button>
                <button onClick={() => refreshPlaceGoogle(p.id)} title="Refresh Google reviews" data-testid={`refresh-google-${p.slug}`} className="rounded-full p-2 hover:bg-muted"><RefreshCw size={15} /></button>
                <button onClick={() => setEditing({ kind: "place", data: { ...p } })} data-testid={`admin-edit-place-${p.slug}`} className="rounded-full p-2 hover:bg-muted"><Pencil size={15} /></button>
                <button onClick={() => delPlace(p.id)} className="rounded-full p-2 text-destructive hover:bg-muted"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* EVENTS */}
      {tab === "Events" && (
        <div>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <button onClick={() => setEditing({ kind: "event", data: { category: "Entertainment", start_datetime: new Date().toISOString().slice(0, 16) } })}
              data-testid="admin-new-event" className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"><Plus size={16} /> New Event</button>
            <div className="ml-auto flex rounded-full border border-border bg-card p-1">
              <button onClick={() => setEventView("list")} className={`rounded-full px-3 py-1.5 text-sm font-medium ${eventView === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>List</button>
              <button onClick={() => setEventView("calendar")} data-testid="admin-events-calendar" className={`rounded-full px-3 py-1.5 text-sm font-medium ${eventView === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Calendar</button>
            </div>
          </div>
          {eventView === "list" ? (
            <div className="space-y-2">
              {events.map((e) => (
                <div key={e.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3">
                  <img src={e.cover_image} alt="" className="h-12 w-12 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1"><p className="truncate font-semibold">{e.title}</p><p className="text-xs text-muted-foreground">{e.category} · {new Date(e.start_datetime).toLocaleString()}</p></div>
                  <Toggle on={e.is_featured} onClick={() => patchEvent(e.id, { is_featured: !e.is_featured })}><Star size={12} /> Featured</Toggle>
                  <Toggle on={e.is_sponsored} onClick={() => patchEvent(e.id, { is_sponsored: !e.is_sponsored })}><Sparkles size={12} /> Promoted</Toggle>
                  <Toggle on={e.is_hidden} onClick={() => patchEvent(e.id, { is_hidden: !e.is_hidden })}>{e.is_hidden ? <EyeOff size={12} /> : <Eye size={12} />} {e.is_hidden ? "Hidden" : "Visible"}</Toggle>
                  <button onClick={() => setEditing({ kind: "event", data: { ...e, start_datetime: e.start_datetime?.slice(0, 16) } })} data-testid={`admin-edit-event-${e.id}`} className="rounded-full p-2 hover:bg-muted"><Pencil size={15} /></button>
                  <button onClick={() => delEvent(e.id)} className="rounded-full p-2 text-destructive hover:bg-muted"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="rounded-full px-3 py-1 hover:bg-muted">‹</button>
                <span className="font-serif text-xl font-semibold">{cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
                <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="rounded-full px-3 py-1 hover:bg-muted">›</button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-muted-foreground">{["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="py-1">{d}</div>)}</div>
              <div className="grid grid-cols-7 gap-1">
                {days.map((d, i) => {
                  const inMonth = d.getMonth() === cursor.getMonth();
                  const dayEvents = eventsByDay[d.toDateString()] || [];
                  return (
                    <div key={i} className={`min-h-[70px] rounded-lg border p-1 ${inMonth ? "border-border" : "border-transparent opacity-40"}`}>
                      <span className="text-xs font-medium">{d.getDate()}</span>
                      <div className="mt-0.5 space-y-0.5">
                        {dayEvents.slice(0, 2).map((e) => (
                          <button key={e.id} onClick={() => setEditing({ kind: "event", data: { ...e, start_datetime: e.start_datetime?.slice(0, 16) } })}
                            className="block w-full truncate rounded px-1 text-left text-[9px] font-medium text-white" style={{ backgroundColor: CATEGORY_COLORS[e.category] }}>{e.title}</button>
                        ))}
                        {dayEvents.length > 2 && <span className="text-[9px] text-muted-foreground">+{dayEvents.length - 2}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* REVIEWS */}
      {tab === "Reviews" && (
        <div className="space-y-2">
          {reviews.length === 0 && <p className="text-muted-foreground">No community reviews yet.</p>}
          {reviews.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{r.user_name || "User"}</span>
                <span className="flex items-center gap-0.5 text-sm"><Star size={13} className="fill-accent text-accent" /> {r.rating}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.status === "approved" ? "bg-secondary/15 text-secondary" : r.status === "pending" ? "bg-accent/20 text-accent-foreground" : "bg-destructive/15 text-destructive"}`}>{r.status}</span>
                <span className="ml-auto text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.title && <p className="mt-1 font-medium">{r.title}</p>}
              {r.body && <p className="text-sm text-muted-foreground">{r.body}</p>}
              {r.photos?.length > 0 && <div className="mt-2 flex gap-2">{r.photos.map((ph, i) => <img key={i} src={ph} alt="" className="h-16 w-16 rounded-lg object-cover" />)}</div>}
              <div className="mt-2 flex gap-2">
                <button onClick={() => moderate(r.id, "approved")} data-testid={`approve-review-${r.id}`} className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground"><Check size={12} /> Approve</button>
                <button onClick={() => moderate(r.id, "rejected")} className="flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-semibold"><Ban size={12} /> Reject</button>
                <button onClick={() => moderate(r.id, "hidden")} className="flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-semibold"><EyeOff size={12} /> Hide</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ADS */}
      {tab === "Ads" && (
        <div>
          <button onClick={() => setEditing({ kind: "ad", data: { type: "native_ad", is_active: true, priority: 5 } })}
            data-testid="admin-new-ad" className="mb-4 flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"><Plus size={16} /> New Ad / Sponsorship</button>
          <div className="space-y-2">
            {ads.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3">
                {a.image && <img src={a.image} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{a.title}</p>
                  <p className="text-xs text-muted-foreground">{a.type} · priority {a.priority}{a.end_date ? ` · ends ${new Date(a.end_date).toLocaleDateString()}` : ""}</p>
                </div>
                <Toggle on={a.is_active} onClick={() => patchAd(a.id, { is_active: !a.is_active })}>{a.is_active ? "Active" : "Paused"}</Toggle>
                <button onClick={() => setEditing({ kind: "ad", data: { ...a, start_date: a.start_date?.slice(0, 16), end_date: a.end_date?.slice(0, 16) } })} className="rounded-full p-2 hover:bg-muted"><Pencil size={15} /></button>
                <button onClick={() => delAd(a.id)} className="rounded-full p-2 text-destructive hover:bg-muted"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* USERS */}
      {tab === "Users" && (
        <div>
          <div className="relative mb-4 md:max-w-xs">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search users…" data-testid="admin-user-search"
              className="w-full rounded-full border border-border bg-card py-2 pl-9 pr-4 text-sm outline-none focus:border-primary" />
          </div>
          <div className="mb-6 rounded-2xl border border-border bg-card p-4" data-testid="admin-invite-panel">
            <h3 className="mb-1 flex items-center gap-2 font-semibold"><Shield size={16} /> Invite a new admin</h3>
            <p className="mb-3 text-xs text-muted-foreground">Create a one-time invite. Leave the email blank to just get a copyable link, or enter an email to also send it directly.</p>
            <div className="flex flex-wrap gap-2">
              <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="their@email.com (optional)"
                data-testid="admin-invite-email" className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} data-testid="admin-invite-role"
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
                <option value="admin">Admin — full access</option>
                <option value="editor">Editor — add & edit listings only</option>
              </select>
              <button onClick={createInvite} data-testid="admin-invite-create" className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><Plus size={14} /> Create invite</button>
            </div>
            {inviteResult && (
              <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="admin-invite-result">
                <p className="text-xs font-semibold text-muted-foreground">{inviteResult.emailed ? `Emailed to ${inviteResult.email} — or share this link:` : "Share this invite link:"}</p>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs">{inviteResult.link}</code>
                  <button onClick={() => copyInviteLink(inviteResult.link)} data-testid="admin-invite-copy" className="rounded-full border border-border px-3 py-1 text-xs font-semibold hover:border-primary">Copy</button>
                </div>
              </div>
            )}
            {invites.filter((iv) => !iv.accepted).length > 0 && (
              <div className="mt-4 space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground">Pending invites</p>
                {invites.filter((iv) => !iv.accepted).map((iv) => (
                  <div key={iv.id} className="flex items-center gap-2 text-xs" data-testid={`admin-invite-${iv.id}`}>
                    <span className="flex-1 truncate">{iv.email || "Link invite"} · <strong className="uppercase">{iv.role || "admin"}</strong> · expires {new Date(iv.expires_at).toLocaleDateString()}</span>
                    <button onClick={() => copyInviteLink(`${window.location.origin}/join-admin?token=${iv.token}`)} className="rounded-full border border-border px-2 py-0.5 font-semibold hover:border-primary">Copy link</button>
                    <button onClick={() => revokeInvite(iv.id)} data-testid={`admin-invite-revoke-${iv.id}`} className="rounded-full border border-border px-2 py-0.5 font-semibold text-destructive hover:border-destructive">Revoke</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            {filteredUsers.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`admin-user-${u.id}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground">{u.name?.[0]?.toUpperCase()}</div>
                <div className="min-w-0 flex-1"><p className="truncate font-semibold">{u.name}</p><p className="truncate text-xs text-muted-foreground">{u.email}</p></div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.role === "admin" ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`}>{u.role}</span>
                {u.role === "admin"
                  ? <button onClick={() => setRole(u.id, "user")} className="rounded-full border border-border px-3 py-1 text-xs font-semibold">Make user</button>
                  : <button onClick={() => setRole(u.id, "admin")} data-testid={`make-admin-${u.id}`} className="rounded-full border border-border px-3 py-1 text-xs font-semibold">Make admin</button>}
                <button onClick={() => delUser(u.id)} className="rounded-full p-2 text-destructive hover:bg-muted"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SITE SETTINGS */}
      {tab === "Site" && settings && (
        <div className="max-w-2xl" data-testid="admin-site-settings">
          <h2 className="mb-1 font-serif text-2xl font-bold">Homepage hero</h2>
          <p className="mb-5 text-sm text-muted-foreground">Edit the big banner image and text on the home page. Changes go live after you save.</p>

          <div className="mb-5 overflow-hidden rounded-2xl border border-border">
            <div className="relative">
              <img src={settings.hero_image} alt="Hero preview" className="h-52 w-full object-cover" data-testid="site-hero-preview" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-0 p-4">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-white/90">{settings.hero_eyebrow}</span>
                <p className="font-serif text-xl font-bold text-white">{settings.hero_title}</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Hero image URL <span className="font-normal">(or use a video instead — see below)</span></span>
              <input value={settings.hero_image || ""} data-testid="site-hero-image"
                onChange={(e) => setSettings({ ...settings, hero_image: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </label>
            <label className="flex w-fit cursor-pointer items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold hover:border-primary">
              <ImageDown size={15} /> Upload an image
              <input type="file" accept="image/*" className="hidden" onChange={uploadHeroImage} data-testid="site-hero-upload" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Hero background video URL <span className="font-normal">(optional — MP4 or YouTube; loops muted, overrides the image)</span></span>
              <input value={settings.hero_video || ""} data-testid="site-hero-video"
                onChange={(e) => setSettings({ ...settings, hero_video: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </label>
            <div className="flex flex-wrap gap-2">
              <label className="flex w-fit cursor-pointer items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold hover:border-primary">
                <VideoIcon size={15} /> Upload a video
                <input type="file" accept="video/*" className="hidden" onChange={uploadHeroVideo} data-testid="site-hero-video-upload" />
              </label>
              {settings.hero_video && (
                <button onClick={() => setSettings({ ...settings, hero_video: "" })} data-testid="site-hero-video-clear"
                  className="rounded-full border border-border px-4 py-2 text-sm font-semibold text-muted-foreground hover:border-destructive hover:text-destructive">Remove video</button>
              )}
            </div>
            <Field label="Eyebrow (small label above title)" value={settings.hero_eyebrow || ""} data-testid="site-hero-eyebrow"
              onChange={(e) => setSettings({ ...settings, hero_eyebrow: e.target.value })} />
            <Field label="Title" value={settings.hero_title || ""} data-testid="site-hero-title"
              onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })} />
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Subtitle</span>
              <textarea value={settings.hero_subtitle || ""} rows={2} data-testid="site-hero-subtitle"
                onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </label>
            <button onClick={saveSettings} disabled={savingSettings} data-testid="site-save-btn"
              className="flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {savingSettings ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save homepage
            </button>
          </div>

          <div className="mt-10 border-t border-border pt-8">
            <h2 className="mb-1 font-serif text-2xl font-bold">Admin notifications</h2>
            <p className="mb-5 text-sm text-muted-foreground">Choose where alerts go and which events email you. Leave the address blank to turn all notifications off.</p>
            <div className="space-y-4">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-muted-foreground">Notification email</span>
                <input type="email" value={settings.notification_email || ""} data-testid="site-notify-email"
                  placeholder="alerts@yourdomain.com"
                  onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                  className="w-full max-w-md rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              </label>

              <div className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                <div>
                  <p className="text-sm font-semibold">New business / event submissions</p>
                  <p className="text-xs text-muted-foreground">Email me whenever a member submits a listing.</p>
                </div>
                <button type="button" data-testid="site-notify-submission" role="switch" aria-checked={!!settings.notify_new_submission}
                  onClick={() => setSettings({ ...settings, notify_new_submission: !settings.notify_new_submission })}
                  className={`relative h-6 w-11 rounded-full transition-colors ${settings.notify_new_submission ? "bg-primary" : "bg-muted"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${settings.notify_new_submission ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                <div>
                  <p className="text-sm font-semibold">New community reviews</p>
                  <p className="text-xs text-muted-foreground">Email me whenever someone posts a review.</p>
                </div>
                <button type="button" data-testid="site-notify-review" role="switch" aria-checked={!!settings.notify_new_review}
                  onClick={() => setSettings({ ...settings, notify_new_review: !settings.notify_new_review })}
                  className={`relative h-6 w-11 rounded-full transition-colors ${settings.notify_new_review ? "bg-primary" : "bg-muted"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${settings.notify_new_review ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                <div>
                  <p className="text-sm font-semibold">New sponsored purchases</p>
                  <p className="text-xs text-muted-foreground">Email me whenever a business pays for a featured placement.</p>
                </div>
                <button type="button" data-testid="site-notify-sponsor" role="switch" aria-checked={!!settings.notify_sponsor_purchase}
                  onClick={() => setSettings({ ...settings, notify_sponsor_purchase: !settings.notify_sponsor_purchase })}
                  className={`relative h-6 w-11 rounded-full transition-colors ${settings.notify_sponsor_purchase ? "bg-primary" : "bg-muted"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${settings.notify_sponsor_purchase ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>

              <button onClick={saveSettings} disabled={savingSettings} data-testid="site-notify-save-btn"
                className="flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
                {savingSettings ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save notifications
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "Layout" && settings && (
        <div data-testid="admin-layout-settings">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="mb-1 font-serif text-2xl font-bold">Page layout</h2>
              <p className="text-sm text-muted-foreground">Show, hide and reorder everything on the site — no code needed. Click Save at the bottom to publish your changes.</p>
            </div>
            <button onClick={() => setShowPreview((v) => !v)} data-testid="layout-preview-toggle"
              className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${showPreview ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-foreground hover:border-primary"}`}>
              <Monitor size={15} /> {showPreview ? "Hide preview" : "Live preview"}
            </button>
          </div>

          <div className={`grid gap-8 ${showPreview ? "lg:grid-cols-2" : ""}`}>
            <div className="max-w-2xl space-y-10">
          {/* Home rows */}
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold">Home page rows</h3>
              <button onClick={() => resetLayout("home_sections")} data-testid="layout-reset-home_sections" className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary"><RotateCcw size={12} /> Reset</button>
            </div>
            <div className="space-y-2">
              {(settings.home_sections || []).map((it, i, a) => layoutRow("home_sections", it, i, a.length, false))}
            </div>
          </section>

          {/* Featured & Sponsored order */}
          <section data-testid="layout-featured-section">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-semibold"><Star size={16} /> Featured & Sponsored listings</h3>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">Pin listings and drag them into order with the arrows. Click Save & publish to lock the order.</p>
            <div className="space-y-2">
              {orderedFeatured.length === 0 && <p className="text-sm text-muted-foreground">No featured listings yet — pin one below.</p>}
              {orderedFeatured.map((p, i) => (
                <div key={p.id} className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2" data-testid={`layout-featured-row-${p.id}`}>
                  <div className="flex flex-col">
                    <button disabled={i === 0} onClick={() => moveFeatured(i, -1)} data-testid={`layout-featured-up-${p.id}`} className="text-muted-foreground hover:text-primary disabled:opacity-30"><ArrowUp size={14} /></button>
                    <button disabled={i === orderedFeatured.length - 1} onClick={() => moveFeatured(i, 1)} data-testid={`layout-featured-down-${p.id}`} className="text-muted-foreground hover:text-primary disabled:opacity-30"><ArrowDown size={14} /></button>
                  </div>
                  {p.cover_image && <img src={p.cover_image} alt="" className="h-9 w-9 rounded object-cover" />}
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{p.name}</span>
                    {p.is_sponsored && p.sponsored_until && (
                      <span className="text-[11px] font-semibold text-accent-foreground/80" data-testid={`layout-sponsor-countdown-${p.id}`}>
                        Sponsored · {Math.max(0, Math.ceil((new Date(p.sponsored_until) - Date.now()) / 86400000))} days left
                        {p.auto_renew && <span className="ml-1 text-secondary" data-testid={`layout-autorenew-badge-${p.id}`}>· Auto-renew on</span>}
                      </span>
                    )}
                  </div>
                  {p.auto_renew && (
                    <button onClick={() => cancelAutoRenew(p.id)} data-testid={`layout-cancel-autorenew-${p.id}`}
                      className="rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground hover:border-destructive hover:text-destructive">
                      Turn off auto-renew
                    </button>
                  )}
                  <div className="flex overflow-hidden rounded-full border border-border text-[11px] font-semibold" data-testid={`layout-featured-badge-${p.id}`}>
                    <button onClick={() => setSponsored(p.id, false)} data-testid={`layout-featured-badge-featured-${p.id}`}
                      className={`px-2.5 py-1 ${!p.is_sponsored ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Featured</button>
                    <button onClick={() => setSponsored(p.id, true, sponsorDays[p.id] ?? 30)} data-testid={`layout-featured-badge-sponsored-${p.id}`}
                      className={`px-2.5 py-1 ${p.is_sponsored ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}>Sponsored</button>
                  </div>
                  {p.is_sponsored && (
                    <div className="flex items-center gap-1 text-[11px]" data-testid={`layout-sponsor-expiry-${p.id}`}>
                      <input type="number" min="1" value={sponsorDays[p.id] ?? 30} onChange={(e) => setSponsorDays((d) => ({ ...d, [p.id]: e.target.value }))}
                        data-testid={`layout-sponsor-days-${p.id}`} className="w-12 rounded border border-border bg-background px-1 py-0.5 text-center" />
                      <span className="text-muted-foreground">days</span>
                      <button onClick={() => setSponsored(p.id, true, sponsorDays[p.id] ?? 30)} data-testid={`layout-sponsor-apply-${p.id}`} className="rounded-full border border-border px-2 py-0.5 font-semibold hover:border-primary">Set</button>
                      <button onClick={() => extendSponsor(p)} data-testid={`layout-sponsor-extend-${p.id}`} className="rounded-full bg-accent px-2 py-0.5 font-semibold text-accent-foreground hover:opacity-90">+30d</button>
                    </div>
                  )}
                  <button onClick={() => pinPlace(p.id, false)} data-testid={`layout-featured-unpin-${p.id}`}
                    className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:border-destructive hover:text-destructive"><X size={12} /> Unpin</button>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-muted-foreground">Pin a listing to Featured</span>
                <select data-testid="layout-featured-add" defaultValue=""
                  onChange={(e) => { if (e.target.value) { pinPlace(e.target.value, true); e.target.value = ""; } }}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
                  <option value="">Choose a listing…</option>
                  {places.filter((p) => !p.is_featured && p.status === "published").sort((a, b) => a.name.localeCompare(b.name)).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          {/* Custom home rows */}
          <section data-testid="layout-custom-rows-section">
            <h3 className="mb-1 flex items-center gap-2 font-semibold"><Layers size={16} /> Custom home rows</h3>
            <p className="mb-3 text-xs text-muted-foreground">Build your own homepage row from any category or tag. New rows appear in "Home page rows" above where you can reorder or hide them.</p>
            <div className="space-y-2">
              {(settings.custom_rows || []).map((r) => (
                <div key={r.id} className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2" data-testid={`layout-custom-row-${r.id}`}>
                  <div className="flex-1">
                    <span className="text-sm font-medium">{r.title}</span>
                    <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{r.source_type === "tag" ? "Tag" : "Category"}: {r.value}</span>
                    {customCounts[r.id] === 0 ? (
                      <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid={`layout-custom-empty-${r.id}`}>No listings match yet — this row stays hidden until one does</span>
                    ) : customCounts[r.id] > 0 ? (
                      <span className="ml-2 text-[11px] text-muted-foreground">{customCounts[r.id]} listing{customCounts[r.id] === 1 ? "" : "s"}</span>
                    ) : null}
                  </div>
                  <button onClick={() => removeCustomRow(r.id)} data-testid={`layout-custom-remove-${r.id}`}
                    className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:border-destructive hover:text-destructive"><Trash2 size={12} /> Remove</button>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-2 rounded-xl border border-dashed border-border p-3">
              <input value={newRow.title} onChange={(e) => setNewRow({ ...newRow, title: e.target.value })} placeholder="Row title (e.g. Best Brunch Spots)"
                data-testid="layout-custom-title" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              <div className="flex gap-2">
                <select value={newRow.source_type} data-testid="layout-custom-source"
                  onChange={(e) => setNewRow({ ...newRow, source_type: e.target.value, value: e.target.value === "category" ? "Food & Drink" : "" })}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
                  <option value="category">By category</option>
                  <option value="tag">By tag</option>
                </select>
                {newRow.source_type === "category" ? (
                  <select value={newRow.value} onChange={(e) => setNewRow({ ...newRow, value: e.target.value })} data-testid="layout-custom-value-category"
                    className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                ) : (
                  <input value={newRow.value} onChange={(e) => setNewRow({ ...newRow, value: e.target.value })} placeholder="tag (e.g. date-night)"
                    data-testid="layout-custom-value-tag" className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                )}
              </div>
              <button onClick={addCustomRow} data-testid="layout-custom-add"
                className="flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground"><Plus size={14} /> Add row</button>
            </div>
          </section>

          {/* Mood buttons */}
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold">Mood buttons</h3>
              <button onClick={() => resetLayout("moods")} data-testid="layout-reset-moods" className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary"><RotateCcw size={12} /> Reset</button>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">Rename, reorder or hide the "What's the mood?" chips on the homepage.</p>
            <div className="space-y-2">
              {(settings.moods || []).map((it, i, a) => layoutRow("moods", it, i, a.length, true))}
            </div>
          </section>

          {/* Navigation */}
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold">Navigation menu</h3>
              <button onClick={() => resetLayout("nav_items")} data-testid="layout-reset-nav_items" className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary"><RotateCcw size={12} /> Reset</button>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">Controls the top and bottom navigation links.</p>
            <div className="space-y-2">
              {(settings.nav_items || []).map((it, i, a) => layoutRow("nav_items", it, i, a.length, true))}
            </div>
          </section>

          {/* Trails */}
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold">Trails lineup</h3>
              <button onClick={() => resetLayout("trails")} data-testid="layout-reset-trails" className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary"><RotateCcw size={12} /> Reset</button>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">Which curated trails appear on the Trails page, and in what order.</p>
            <div className="space-y-2">
              {(settings.trails || []).map((it, i, a) => layoutRow("trails", it, i, a.length, false))}
            </div>
          </section>

          {/* Colors & fonts */}
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-semibold"><Palette size={16} /> Colors & fonts</h3>
              <button onClick={resetTheme} data-testid="layout-reset-theme" className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary"><RotateCcw size={12} /> Reset</button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-muted-foreground">Primary color</span>
                <div className="flex items-center gap-2">
                  <input type="color" value={settings.theme?.primary_hex || DEFAULT_THEME.primary_hex} onChange={(e) => setTheme({ primary_hex: e.target.value })}
                    data-testid="layout-theme-primary" className="h-10 w-14 cursor-pointer rounded border border-border bg-background" />
                  <span className="text-sm text-muted-foreground">{settings.theme?.primary_hex || DEFAULT_THEME.primary_hex}</span>
                </div>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-muted-foreground">Accent color</span>
                <div className="flex items-center gap-2">
                  <input type="color" value={settings.theme?.accent_hex || DEFAULT_THEME.accent_hex} onChange={(e) => setTheme({ accent_hex: e.target.value })}
                    data-testid="layout-theme-accent" className="h-10 w-14 cursor-pointer rounded border border-border bg-background" />
                  <span className="text-sm text-muted-foreground">{settings.theme?.accent_hex || DEFAULT_THEME.accent_hex}</span>
                </div>
              </label>
            </div>
            <label className="mt-4 block text-sm">
              <span className="mb-1 flex items-center gap-1.5 font-medium text-muted-foreground"><Type size={14} /> Font pairing</span>
              <select value={settings.theme?.font || "classic"} onChange={(e) => setTheme({ font: e.target.value })} data-testid="layout-theme-font"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
                {FONT_PAIRINGS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </label>
          </section>

          <button onClick={saveSettings} disabled={savingSettings} data-testid="layout-save-btn"
            className="flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            {savingSettings ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save & publish layout
          </button>
            </div>

            {showPreview && (
              <div className="hidden lg:block">
                <div className="sticky top-20">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Live homepage preview</span>
                    <div className="flex overflow-hidden rounded-full border border-border text-xs font-semibold">
                      <button onClick={() => setPreviewDevice("desktop")} data-testid="layout-preview-desktop"
                        className={`px-3 py-1 ${previewDevice === "desktop" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Desktop</button>
                      <button onClick={() => setPreviewDevice("mobile")} data-testid="layout-preview-mobile"
                        className={`px-3 py-1 ${previewDevice === "mobile" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Mobile</button>
                    </div>
                  </div>
                  {previewDevice === "desktop" ? (
                    <div className="h-[72vh] overflow-y-auto overflow-x-hidden rounded-2xl border border-border bg-background" data-testid="layout-preview-panel">
                      <div style={{ transform: "scale(0.5)", transformOrigin: "top left", width: "200%", pointerEvents: "none" }}>
                        <SettingsContext.Provider value={{ settings, reloadSettings: () => {} }}>
                          <Home />
                        </SettingsContext.Provider>
                      </div>
                    </div>
                  ) : (
                    <div data-testid="layout-preview-panel-mobile">
                      <div className="mx-auto w-[390px] max-w-full overflow-hidden rounded-[2rem] border-4 border-foreground/80 bg-background shadow-xl">
                        <iframe key={previewNonce} title="Mobile preview" src={lastSection ? `/#section-${lastSection}` : "/"} className="h-[70vh] w-[390px] border-0" />
                      </div>
                      <p className="mt-2 text-center text-xs text-muted-foreground">Mobile view shows your <strong>published</strong> homepage. Click <strong>Save &amp; publish</strong> to refresh it with your latest edits.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {tab === "Videos" && (
        <div className="max-w-3xl space-y-6" data-testid="admin-videos">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="mb-1 font-serif text-2xl font-bold">Videos</h2>
              <p className="text-sm text-muted-foreground">Add TikTok, Instagram Reels, YouTube/Shorts, Facebook links, or upload a clip. Mark one as Featured to spotlight it on the home page. Use the arrows to reorder.</p>
            </div>
            <button onClick={backfillThumbnails} data-testid="video-backfill-thumbs" className="shrink-0 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary">Refresh thumbnails</button>
          </div>
          <div className="space-y-3 rounded-2xl border border-border bg-card p-4">
            <Field label="Title" data-testid="video-title" value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} />
            <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Platform</span>
              <select value={videoForm.platform} data-testid="video-platform" onChange={(e) => setVideoForm({ ...videoForm, platform: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                <option value="youtube">YouTube / Shorts</option><option value="tiktok">TikTok</option><option value="instagram">Instagram Reel</option><option value="facebook">Facebook (post or video)</option><option value="file">Uploaded file</option>
              </select></label>
            <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Video URL</span>
              <input value={videoForm.url} data-testid="video-url" onChange={(e) => setVideoForm({ ...videoForm, url: e.target.value })} placeholder="Paste the video link" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" /></label>
            <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Or paste embed code <span className="font-normal">(iframe/blockquote from TikTok, YouTube, Facebook, Instagram, etc. — overrides the URL)</span></span>
              <textarea value={videoForm.embed_html || ""} data-testid="video-embed-html" rows={3} onChange={(e) => setVideoForm({ ...videoForm, embed_html: e.target.value })} placeholder="&lt;blockquote class=... or &lt;iframe ...&gt;&lt;/iframe&gt;" className="w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs outline-none focus:border-primary" /></label>
            <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Poster image <span className="font-normal">(shown on the Watch row — YouTube auto-fills; add one for TikTok so the tile isn't blank)</span></span>
              <div className="flex flex-wrap items-center gap-2">
                <input value={videoForm.thumbnail || ""} data-testid="video-thumbnail" onChange={(e) => setVideoForm({ ...videoForm, thumbnail: e.target.value })} placeholder="Paste an image URL, or upload →" className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                <label className="flex cursor-pointer items-center gap-1 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary">
                  <ImageDown size={14} /> Upload
                  <input type="file" accept="image/*" className="hidden" onChange={uploadVideoThumb} data-testid="video-thumbnail-upload" />
                </label>
                {videoForm.thumbnail && <img src={videoForm.thumbnail} alt="" className="h-12 w-12 rounded-lg object-cover" />}
              </div></label>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex cursor-pointer items-center gap-1 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary">
                <VideoIcon size={14} /> Upload a clip
                <input type="file" accept="video/*" className="hidden" onChange={uploadVideoFile} data-testid="video-file-upload" />
              </label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!videoForm.is_featured} data-testid="video-featured" onChange={(e) => setVideoForm({ ...videoForm, is_featured: e.target.checked })} /> Featured on home</label>
              <button onClick={saveVideo} data-testid="video-save-btn" className="ml-auto rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">{videoForm.id ? "Update video" : "Add video"}</button>
              {videoForm.id && <button onClick={() => setVideoForm({ title: "", platform: "youtube", url: "", embed_html: "", thumbnail: "", is_featured: false })} className="rounded-full border border-border px-4 py-2 text-sm font-semibold">Cancel</button>}
            </div>
          </div>
          {pendingVideos.length > 0 && (
            <div className="space-y-2 rounded-2xl border border-primary/40 bg-primary/5 p-4" data-testid="admin-pending-videos">
              <h3 className="flex items-center gap-2 font-serif text-lg font-bold">
                <Send size={16} className="text-primary" /> Community submissions
                <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">{pendingVideos.length}</span>
              </h3>
              <p className="text-xs text-muted-foreground">Videos sent in by locals. Approve to publish to the Watch section, or reject to delete.</p>
              {pendingVideos.map((v) => (
                <div key={v.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`pending-video-${v.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{v.title}</p>
                    <p className="truncate text-xs text-muted-foreground">{v.platform}{v.url ? ` · ${v.url}` : " · (embed code)"}</p>
                    <p className="text-xs text-muted-foreground">by {v.submitter_name || "a local"}{v.submitter_email ? ` · ${v.submitter_email}` : ""}{v.submitter_note ? ` — “${v.submitter_note}”` : ""}</p>
                  </div>
                  {v.url && <a href={v.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-primary hover:underline">Preview</a>}
                  <button onClick={() => approveVideo(v.id)} data-testid={`approve-video-${v.id}`} className="flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"><Check size={13} /> Approve</button>
                  <button onClick={() => rejectVideo(v.id)} data-testid={`reject-video-${v.id}`} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-muted"><X size={13} /> Reject</button>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-2" data-testid="admin-video-list">
            {videos.length === 0 && <p className="text-sm text-muted-foreground">No videos yet — add one above.</p>}
            {videos.map((v, i) => (
              <div key={v.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`admin-video-${v.id}`}>
                <div className="flex items-center gap-1">
                  <button disabled={i === 0} onClick={() => moveVideo(i, -1)} title="Move left (earlier)" data-testid={`video-left-${v.id}`} className="rounded-full border border-border p-1.5 text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-30"><ArrowLeft size={14} /></button>
                  <button disabled={i === videos.length - 1} onClick={() => moveVideo(i, 1)} title="Move right (later)" data-testid={`video-right-${v.id}`} className="rounded-full border border-border p-1.5 text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-30"><ArrowRight size={14} /></button>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{v.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{v.platform} · {v.url}</p>
                </div>
                <Toggle on={v.is_featured} testid={`video-feature-toggle-${v.id}`} onClick={() => toggleVideoFeature(v)}><Star size={12} /> Featured</Toggle>
                <button onClick={() => setVideoForm({ id: v.id, title: v.title, platform: v.platform, url: v.url, embed_html: v.embed_html || "", thumbnail: v.thumbnail || "", is_featured: v.is_featured })} data-testid={`video-edit-${v.id}`} className="rounded-full p-2 hover:bg-muted"><Pencil size={15} /></button>
                <button onClick={() => deleteVideo(v.id)} data-testid={`video-delete-${v.id}`} className="rounded-full p-2 text-destructive hover:bg-muted"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "Health" && (
        <div className="max-w-3xl space-y-4" data-testid="admin-health">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="mb-1 font-serif text-2xl font-bold">Data check</h2>
              <p className="text-sm text-muted-foreground">Listings & events missing key info. Fix them before visitors see bad data.</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={bulkFill} disabled={bulkFilling} data-testid="health-bulkfill-btn" className="flex items-center gap-1.5 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary disabled:opacity-60">
                {bulkFilling ? <><Loader2 size={14} className="animate-spin" /> Filling…</> : <><Download size={14} /> Auto-fill from Google</>}
              </button>
              <button onClick={loadHealth} data-testid="health-scan-btn" className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">{health ? "Rescan" : "Scan"}</button>
            </div>
          </div>
          {bulkFilling && <p className="text-xs text-muted-foreground" data-testid="health-bulkfill-status">{bulkMsg}</p>}
          {health && (
            <>
              <div className="flex flex-wrap gap-2 text-xs">
                {[["all", `${health.flagged} of ${health.total_places} places`], ["no_hours", `No hours ${health.counts.no_hours}`], ["no_photo", `No photo ${health.counts.no_photo}`], ["no_description", `No description ${health.counts.no_description}`], ["no_location", `No location ${health.counts.no_location}`]].map(([key, label]) => (
                  <button key={key} onClick={() => setHealthFilter(key)} data-testid={`health-filter-${key}`}
                    className={`rounded-full px-3 py-1 font-semibold ${healthFilter === key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>{label}</button>
                ))}
              </div>
              {(healthFilter === "all" ? health.places : health.places.filter((p) => p.issues.includes(healthFilter))).length === 0 && <p className="text-sm text-muted-foreground" data-testid="health-empty">No listings match this filter — nice.</p>}
              {(healthFilter === "all" ? health.places : health.places.filter((p) => p.issues.includes(healthFilter))).map((p) => (
                <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`health-place-${p.slug}`}>
                  {p.cover_image ? <img src={p.cover_image} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground"><ImageIcon size={16} /></div>}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{p.name}</p>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {p.issues.map((iss) => <span key={iss} className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive">{HEALTH_LABELS[iss] || iss}</span>)}
                    </div>
                  </div>
                  <a href={`/place/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-primary hover:underline">View</a>
                  <button onClick={() => { const full = places.find((x) => x.id === p.id); if (full) { setTab("Places"); setEditing({ kind: "place", data: { ...full } }); } }} data-testid={`health-edit-${p.slug}`} className="flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"><Pencil size={13} /> Fix</button>
                </div>
              ))}
              {health.events?.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="health-events">
                  <h3 className="font-serif text-lg font-bold">Events needing attention <span className="text-sm font-normal text-muted-foreground">({health.events_flagged})</span></h3>
                  {health.events.map((e) => (
                    <div key={e.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`health-event-${e.id}`}>
                      {e.cover_image ? <img src={e.cover_image} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground"><CalendarDays size={16} /></div>}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{e.title}</p>
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {e.issues.map((iss) => <span key={iss} className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive">{HEALTH_LABELS[iss] || iss}</span>)}
                        </div>
                      </div>
                      <button onClick={() => { const full = events.find((x) => x.id === e.id); if (full) { setTab("Events"); setEditing({ kind: "event", data: { ...full, start_datetime: full.start_datetime?.slice(0, 16) } }); } }} data-testid={`health-edit-event-${e.id}`} className="flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"><Pencil size={13} /> Fix</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "Photos" && (
        <div className="max-w-3xl space-y-4" data-testid="admin-photo-sweep">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="mb-1 font-serif text-2xl font-bold">Photo cleanup sweep</h2>
              <p className="text-sm text-muted-foreground">Listings still using a stock cover. Load real Google photos and pick the best one — all in one place.</p>
            </div>
            <button onClick={loadNeedsCover} data-testid="sweep-scan-btn" className="shrink-0 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">{needsCover ? "Rescan" : "Scan listings"}</button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={bulkAutoCover} data-testid="sweep-auto-all-btn" className="flex items-center gap-1.5 rounded-full border border-primary bg-primary/5 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10">
              <ImageDown size={15} /> Load &amp; auto-pick best cover for all
            </button>
            <button onClick={clearSeedHours} data-testid="clear-seed-hours-btn" className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold text-muted-foreground hover:border-primary hover:text-primary">
              <Clock size={15} /> Clear placeholder 9–5 hours
            </button>
          </div>
          {needsCover && <p className="text-sm text-muted-foreground" data-testid="sweep-count">{needsCover.count} listing(s) still using a stock photo.</p>}
          <div className="space-y-3">
            {needsCover?.places?.map((p) => (
              <div key={p.id} className="rounded-2xl border border-border bg-card p-3" data-testid={`sweep-place-${p.id}`}>
                <div className="flex items-center gap-3">
                  {p.cover_image && <img src={p.cover_image} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{p.name}</p><p className="text-xs text-muted-foreground">{p.city}</p></div>
                  <button onClick={() => loadSweepPhotos(p.id)} disabled={sweepLoading === p.id} data-testid={`sweep-load-${p.id}`} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary disabled:opacity-50">
                    {sweepLoading === p.id ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />} Load Google photos
                  </button>
                </div>
                {sweepOptions[p.id]?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid={`sweep-options-${p.id}`}>
                    {sweepOptions[p.id].map((ph, idx) => (
                      <button key={ph} onClick={() => applySweepCover(p.id, ph)} data-testid={`sweep-option-${p.id}-${idx}`} className="overflow-hidden rounded-lg hover:ring-2 hover:ring-primary">
                        <img src={ph} alt="" className="h-16 w-16 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 md:items-center md:p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-card p-6 md:rounded-3xl" onClick={(e) => e.stopPropagation()} data-testid="admin-edit-modal">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-serif text-2xl font-bold capitalize">{editing.data.id ? "Edit" : "New"} {editing.kind}</h2>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              {editing.kind === "place" && (<>
                <Field label="Name" value={editing.data.name || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, name: e.target.value } })} />
                <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Category</span>
                  <select value={editing.data.category} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, category: e.target.value } })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                    {CATEGORIES.concat("Other").map((c) => <option key={c}>{c}</option>)}</select></label>
                <div>
                  <span className="mb-1 block text-sm font-medium text-muted-foreground">Sports tags</span>
                  <div className="flex flex-wrap gap-2">
                    {SPORTS_TAGS.map((s) => {
                      const on = (editing.data.tags || []).includes(s);
                      return (
                        <button key={s} type="button" data-testid={`admin-sport-tag-${s.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => {
                            const cur = editing.data.tags || [];
                            const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
                            setEditing({ ...editing, data: { ...editing.data, tags: next } });
                          }}
                          className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-transparent bg-secondary text-secondary-foreground" : "border-border bg-background text-muted-foreground hover:border-primary"}`}>
                          <SportIcon sport={s} className="h-3 w-3" /> {s}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Combine with the <b>Sports</b> category (e.g. Sports + Golf).</p>
                </div>
                <div>
                  <span className="mb-1 block text-sm font-medium text-muted-foreground">Fitness tags</span>
                  <div className="flex flex-wrap gap-2">
                    {FITNESS_TAGS.map((s) => {
                      const on = (editing.data.tags || []).includes(s);
                      return (
                        <button key={s} type="button" data-testid={`admin-fitness-tag-${s.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => {
                            const cur = editing.data.tags || [];
                            const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
                            setEditing({ ...editing, data: { ...editing.data, tags: next } });
                          }}
                          className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-transparent bg-secondary text-secondary-foreground" : "border-border bg-background text-muted-foreground hover:border-primary"}`}>
                          <SportIcon sport={s} className="h-3 w-3" /> {s}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Combine with the <b>Fitness</b> category (e.g. Fitness + Yoga).</p>
                </div>
                <Field label="Tagline" value={editing.data.short_tagline || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, short_tagline: e.target.value } })} />
                <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Description</span>
                  <textarea rows={3} value={editing.data.description || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" /></label>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="City" value={editing.data.city || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, city: e.target.value } })} />
                  <Field label="Price ($-$$$$)" value={editing.data.price_range || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, price_range: e.target.value } })} />
                </div>
                <Field label="Address" value={editing.data.address || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, address: e.target.value } })} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Phone" value={editing.data.phone || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, phone: e.target.value } })} />
                  <Field label="Website" value={editing.data.website || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, website: e.target.value } })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Lat" type="number" value={editing.data.lat || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, lat: parseFloat(e.target.value) } })} />
                  <Field label="Lng" type="number" value={editing.data.lng || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, lng: parseFloat(e.target.value) } })} />
                </div>
                {/* Sports facility details */}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Courts / fields" type="number" value={editing.data.num_courts ?? ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, num_courts: e.target.value === "" ? null : parseInt(e.target.value, 10) } })} />
                  <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Access</span>
                    <select value={editing.data.access_type || ""} data-testid="admin-access-type" onChange={(e) => setEditing({ ...editing, data: { ...editing.data, access_type: e.target.value || null } })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                      <option value="">—</option><option value="public">Public</option><option value="private">Private / Members</option></select></label>
                </div>
                <Field label="Reservation / booking URL" value={editing.data.reservation_url || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, reservation_url: e.target.value } })} />
                {/* Photo manager */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-muted-foreground">Photos</span>
                  <div className="mb-2 flex flex-wrap gap-2">
                    {(editing.data.photos || []).map((ph, idx) => (
                      <div key={ph} className="relative cursor-move" draggable
                        title="Drag to reorder"
                        onDragStart={() => setDragPhoto(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => reorderPhoto(dragPhoto, idx)}>
                        <img src={ph} alt="" data-testid={`admin-photo-${idx}`} className={`h-16 w-16 rounded-lg object-cover ${editing.data.cover_image === ph ? "ring-2 ring-primary" : ""} ${dragPhoto === idx ? "opacity-40" : ""}`} onClick={() => setEditing({ ...editing, data: { ...editing.data, cover_image: ph } })} />
                        {editing.data.cover_image === ph && <span className="absolute bottom-0 left-0 right-0 rounded-b-lg bg-primary/90 py-0.5 text-center text-[9px] font-bold text-primary-foreground">Cover</span>}
                        <button onClick={() => removePhoto(ph)} data-testid={`admin-photo-remove-${idx}`} className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white"><X size={11} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={newPhoto} onChange={(e) => setNewPhoto(e.target.value)} placeholder="Paste image URL" className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                    <button onClick={addPhotoUrl} className="rounded-lg border border-border px-3 text-sm font-semibold">Add</button>
                    <label className={`flex items-center gap-1 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary ${uploadingPhoto ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`} data-testid="admin-place-photo-upload-label">
                      {uploadingPhoto ? <><Loader2 size={14} className="animate-spin" /> Uploading {uploadProgress}%</> : "Upload"}
                      <input type="file" accept="image/*" className="hidden" disabled={uploadingPhoto} data-testid="admin-place-photo-upload" onChange={uploadPlacePhoto} />
                    </label>
                    <button type="button" onClick={pickGooglePhotos} disabled={loadingGPhotos || !editing?.data?.id} data-testid="admin-pick-google-photos"
                      className="flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-sm font-semibold text-muted-foreground hover:border-primary disabled:opacity-50">
                      {loadingGPhotos ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />} Pick Google photos
                    </button>
                  </div>
                  {uploadingPhoto && (
                    <div className="mt-2" data-testid="admin-photo-upload-progress">
                      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary transition-all duration-150" style={{ width: `${uploadProgress}%` }} />
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">Uploading photo… {uploadProgress}%</p>
                    </div>
                  )}
                  {googlePhotos.length > 0 && (
                    <div className="mt-2 rounded-lg border border-border bg-muted/30 p-2" data-testid="google-photo-picker">
                      <p className="mb-1.5 text-xs font-medium text-muted-foreground">Tap a Google photo to set it as the cover:</p>
                      <div className="flex flex-wrap gap-2">
                        {googlePhotos.map((ph, idx) => (
                          <button type="button" key={ph} onClick={() => applyGooglePhoto(ph)} data-testid={`google-photo-option-${idx}`}
                            className={`relative overflow-hidden rounded-lg ${editing.data.cover_image === ph ? "ring-2 ring-primary" : "hover:ring-2 hover:ring-primary/50"}`}>
                            <img src={ph} alt="" className="h-16 w-16 object-cover" />
                            {editing.data.cover_image === ph && <span className="absolute bottom-0 left-0 right-0 bg-primary/90 py-0.5 text-center text-[9px] font-bold text-primary-foreground">Cover</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">Drag photos to reorder the gallery. Tap a photo to set it as the cover. If you don't pick one, the first photo is used automatically.</p>
                </div>
                {/* Weekly hours editor */}
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="block text-sm font-medium text-muted-foreground">Weekly hours</span>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground" title="When on, Google's hourly sync will not overwrite these hours">
                      <input type="checkbox" checked={!!editing.data.hours_locked} data-testid="admin-hours-locked"
                        onChange={(e) => setEditing({ ...editing, data: { ...editing.data, hours_locked: e.target.checked } })} />
                      Keep my custom hours
                    </label>
                  </div>
                  <div className="space-y-1.5">
                    {[["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]].map(([k, lbl]) => {
                      const h = (editing.data.hours || {})[k] || { open: "09:00", close: "17:00", closed: false };
                      const upd = (patch) => { const hrs = { ...(editing.data.hours || {}) }; hrs[k] = { ...h, ...patch }; setEditing({ ...editing, data: { ...editing.data, hours: hrs } }); };
                      return (
                        <div key={k} data-testid={`admin-hours-${k}`} className="flex items-center gap-2 text-sm">
                          <span className="w-9 font-medium">{lbl}</span>
                          <label className="flex items-center gap-1 text-xs text-muted-foreground"><input type="checkbox" checked={!!h.closed} data-testid={`admin-hours-${k}-closed`} onChange={(e) => upd({ closed: e.target.checked })} /> Closed</label>
                          {!h.closed && (<>
                            <input type="time" value={h.open || "09:00"} onChange={(e) => upd({ open: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-1 text-sm" />
                            <span className="text-muted-foreground">–</span>
                            <input type="time" value={h.close || "17:00"} onChange={(e) => upd({ close: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-1 text-sm" />
                          </>)}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Holiday / special hours */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-muted-foreground">Holiday / special hours</span>
                  <p className="mb-2 text-xs text-muted-foreground">One-off dates that override the weekly schedule (e.g. holidays).</p>
                  <div className="mb-2 space-y-1.5">
                    {Object.entries(editing.data.special_hours || {}).sort().map(([date, h]) => (
                      <div key={date} data-testid={`special-hours-row-${date}`} className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
                        <span className="font-semibold">{date}</span>
                        <span className="text-muted-foreground">{h.closed ? "Closed" : dayLabel(h)}{h.note ? ` · ${h.note}` : ""}</span>
                        <button onClick={() => { const s = { ...(editing.data.special_hours || {}) }; delete s[date]; setEditing({ ...editing, data: { ...editing.data, special_hours: s } }); }} className="ml-auto text-destructive"><X size={13} /></button>
                      </div>
                    ))}
                    {Object.keys(editing.data.special_hours || {}).length === 0 && <p className="text-xs text-muted-foreground">No special dates yet.</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="date" value={newSpecial.date} onChange={(e) => setNewSpecial({ ...newSpecial, date: e.target.value })} data-testid="special-hours-date" className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                    <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={newSpecial.closed} onChange={(e) => setNewSpecial({ ...newSpecial, closed: e.target.checked })} data-testid="special-hours-closed" /> Closed</label>
                    {!newSpecial.closed && (<>
                      <input type="time" value={newSpecial.open} onChange={(e) => setNewSpecial({ ...newSpecial, open: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                      <input type="time" value={newSpecial.close} onChange={(e) => setNewSpecial({ ...newSpecial, close: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                    </>)}
                    <input value={newSpecial.note} onChange={(e) => setNewSpecial({ ...newSpecial, note: e.target.value })} placeholder="Note (e.g. Christmas)" className="min-w-[8rem] flex-1 rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                    <button data-testid="special-hours-add" onClick={() => {
                      if (!newSpecial.date) { toast.error("Pick a date"); return; }
                      const s = { ...(editing.data.special_hours || {}) };
                      s[newSpecial.date] = newSpecial.closed
                        ? { closed: true, note: newSpecial.note || null }
                        : { open: newSpecial.open, close: newSpecial.close, closed: false, note: newSpecial.note || null };
                      setEditing({ ...editing, data: { ...editing.data, special_hours: s } });
                      setNewSpecial({ date: "", closed: false, open: "09:00", close: "17:00", note: "" });
                    }} className="rounded-lg border border-border px-3 py-1.5 text-sm font-semibold">Add date</button>
                  </div>
                </div>
              </>)}
              {editing.kind === "event" && (<>
                <Field label="Title" value={editing.data.title || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, title: e.target.value } })} />
                <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Category</span>
                  <select value={editing.data.category} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, category: e.target.value } })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></label>
                <Field label="Start date/time" type="datetime-local" value={editing.data.start_datetime || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, start_datetime: e.target.value } })} />
                <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Description</span>
                  <textarea rows={3} value={editing.data.description || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" /></label>
                <Field label="Venue name" value={editing.data.venue_name || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, venue_name: e.target.value } })} />
                <Field label="Address" value={editing.data.address || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, address: e.target.value } })} />
                <Field label="Cover image URL" value={editing.data.cover_image || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, cover_image: e.target.value } })} />
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex cursor-pointer items-center gap-1 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary">
                    <ImageDown size={14} /> Upload photo
                    <input type="file" accept="image/*" className="hidden" onChange={uploadEventCover} data-testid="event-cover-upload" />
                  </label>
                  {editing.data.cover_image && <img src={editing.data.cover_image} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                </div>
                <Field label="Ticket URL" value={editing.data.ticket_url || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, ticket_url: e.target.value } })} />
                <Field label="Price info" value={editing.data.price_info || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, price_info: e.target.value } })} />
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.data.is_free} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, is_free: e.target.checked } })} /> Free event</label>
              </>)}
              {editing.kind === "ad" && (<>
                <Field label="Title" value={editing.data.title || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, title: e.target.value } })} />
                <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Type</span>
                  <select value={editing.data.type} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, type: e.target.value } })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                    <option value="native_ad">Native Ad</option><option value="featured_place">Featured Place</option><option value="promoted_event">Promoted Event</option></select></label>
                <label className="block text-sm"><span className="mb-1 block font-medium text-muted-foreground">Description</span>
                  <textarea rows={2} value={editing.data.description || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, description: e.target.value } })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" /></label>
                <Field label="Image URL" value={editing.data.image || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, image: e.target.value } })} />
                <Field label="Link URL" value={editing.data.link_url || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, link_url: e.target.value } })} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Start date" type="datetime-local" value={editing.data.start_date || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, start_date: e.target.value } })} />
                  <Field label="End date" type="datetime-local" value={editing.data.end_date || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, end_date: e.target.value } })} />
                </div>
                <Field label="Priority (higher shows first)" type="number" value={editing.data.priority ?? 0} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, priority: parseInt(e.target.value || "0", 10) } })} />
              </>)}
            </div>
            <button onClick={saveEntity} disabled={savingEntity || uploadingPhoto} data-testid="admin-save-btn" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {savingEntity ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : uploadingPhoto ? "Waiting for upload…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
